import { useCallback, useEffect, useRef, useState } from "react";
import api from "api/api";

// mui components
import { styled } from "@mui/material/styles";
import { Button } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import VariableCategoryColorMap from "../../utils/VariableCategoryColorMap";

// common components
import ColumnVisibility from "../common/ColumnVisibility";
import Reset from "./Reset";
import FilterAcrossProductsToggleComponent from "./FilterAcrossProductsToggleComponent";
import MatchNetWeightToggle from "./MatchNetWeightToggle";
import ConsumerFilter from "./ConsumerFilter";
import TableCellWithInnerSeparator from "../common/analysisModule/TableCellWithInnerSeparator";
import TableCellWithOuterSeparator from "../common/analysisModule/TableCellWithOuterSeparator";
import HeaderSampleSize from "../common/analysisModule/HeaderSampleSize";
import ProductInfo from "../common/ProductInfo";
import ActionStandardTooltipData from "../common/ActionStandardTooltipData";
import CorrelationDialogBox from "./Dialog/CorrelationDialogBox";
// child components
import SideBar from "./MatchNetSideBar";
import MatchNetSideNavLayout from "./MatchNetSideNavLayout";
import PropTypes from "prop-types";

// utils
import { getProductScoreWithNaAndRowSpan, getScoreByVariable, getProductColumnClassName, getFormattedScoreOrNA, isProductVisible } from "../../utils/analysisTable/analysisUtils";

// redux
import { useSelector, useDispatch } from "react-redux";
import InterpolationDetailsTooltip from "./InterpolationDetailsTooltip";
import InterpolationDialog from "./InterpolationDialog";
import { showNotification } from "store/features/notificationSlice";
import { 
    loadWeightToggleState, 
    loadProductSecondaryDataState,
    loadCorrelationToggleState, 
    loadSelectedCorrelationMethodState, 
    loadSelectedCorrelationTargetVariableState, 
    loadCorrelationStudyTypeMethodState,
    loadConsumerFiltersState,
    loadInterpolationToggleState,
    loadInterpolationDetailsState,
    // loadCheckedVariablesState
 } from "store/features/matchNetSlice";
import { loadProductsVisibility } from "store/features/productsInfoSlice";

const NoMaxWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
        placement="right"
    />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: "none",
        background: "linear-gradient(90deg, rgba(191, 242, 255, 1) 0%, rgba(195, 215, 255, 1) 100%)",
        borderRadius: "5px",
        padding: "5px 0px",
    },
});

const ActTool = ({ style, data, studyType, studyTypeMethod }) => {
    return (
        <NoMaxWidthTooltip
            title={
                <div>
                    <ActionStandardTooltipData
                        data={data}
                        onlyKpi={false}
                        studyType={studyType}
                        studyTypeMethod={studyTypeMethod}
                    />
                </div>
            }
        >
            <div
                style={{
                    ...style,
                    marginBottom: "8px",
                }}
            ></div>
        </NoMaxWidthTooltip>
    );
};

// Typechecking props for the Study
ActTool.propTypes = {
    style: PropTypes.object,
    data: PropTypes.array,
    studyType: PropTypes.string,
    studyTypeMethod: PropTypes.string,
};

const ovalStyleRed = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "red", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
};
const ovalStyleGreen = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "green", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
};
const ovalStyleWhite = {
    display: "inline-block",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%", // Circle shape
    backgroundColor: "white", // Green for available, red for unavailable
    marginRight: "10px",
    cursor: "pointer",
    border: "1px solid black",
};

const getBackgroundColor = (correlationValue) => {
    const val = parseFloat(correlationValue.toFixed(2));
    if (Number.isNaN(val)) return "#FFFFFF";
    if (val >= 0.7) return "#008651";
    if (val >= 0.4 && val <= 0.69) return "#A3E1D2";
    if (val >= 0.1 && val <= 0.39) return "#FFE300";
    if (val >= 0 && val <= 0.09) return "#FFA91F";
    if (val > -0.4 && val <= -0.01) return "#FF9998";
    if (val >= -0.69 && val <= -0.4) return "#FF544F";
    if (val <= -0.7) return "#EB0000";
    return "#FFFFFF";
};

const CORRELATION_COLOR_RANGES = [
    { label: "0.70 Above", color: "#008651" },
    { label: "0.69 to 0.40", color: "#A3E1D2" },
    { label: "0.39 to 0.10", color: "#FFE300" },
    { label: "0.09 to 0", color: "#FFA91F" },
    { label: "-0.01 to -0.4", color: "#FF9998" },
    { label: "-0.4 to -0.69", color: "#FF544F" },
    { label: "Below -0.70", color: "#EB0000" },
];

function CrossStudyComparisonTable({ dataMappingData = [], variableMapping = [], setVariableMapping = () => {}, removedKpi = [], setRemovedKpi = () => {}, getCrossStudyComparisonData = () => {} }) {
    const tableRef = useRef(null);
    const dispatch = useDispatch();

    const stickyColumns = ["Attribute", "Attribute Area"];
    const moduleName = "matchNetCrossStudyComparison";

    const [productDetailsDialogueOpen, setProductDetailsDialogueOpen] = useState(false);
    const [selectedProductInfo, setSelectedProductInfo] = useState({});

    // action standards data
    const [actionStandardData, setActionStandardData] = useState([]);

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const [correlationResult, setCorrelationResult] = useState(null);
    const [selectedTargetVariable, setSelectedTargetVariable] = useState(null);
    const [selectedCorrelationMethod, setSelectedCorrelationMethod] = useState(null);
    const [activeCorrelationStudies, setActiveCorrelationStudies] = useState([]);

    // KPI sidebar state — each study gets only its own removed variables, not the combined list
    const [selectedVariables, setSelectedVariables] = useState(() =>
        Object.fromEntries(dataMappingData.map((datum) => {
            const defaultCats = new Set((datum.mapping?.default_mapping?.columns ?? []).map(({ macroCategory }) => macroCategory));
            const studyRemoved = (datum.mapping?.all_mapping?.columns ?? [])
                .filter(({ macroCategory }) => !defaultCats.has(macroCategory))
                .flatMap(({ displayLabels }) => displayLabels.map(({ backendVariable }) => backendVariable));
            return [datum.study_type_method_title, studyRemoved];
        }))
    );

    // ── Redux selectors ────────────────────────────
    const productSecondaryData = useSelector((state) => state.matchNetStore.productSecondaryData);
    const defaultProductSecondaryData = useSelector((state) => state.matchNetStore.defaultProductSecondaryData);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const isConsumerFilterLoadingStoreData = useSelector((state) => state.consumerFilterStore.isLoading);
    const lookupSelectedProducts = useSelector((state) => state.matchNetStore.lookupSelectedProducts);
    const interpolationDetailsStoreData = useSelector((state) => state.matchNetStore.interpolationDetails);
    const consumerFilters = useSelector((state) => state.matchNetStore.consumerFilters);

    const visibleProductCount = productSecondaryData.filter((p) => isProductVisible(productColumnVisibility, p)).length;
    const isAnyProductVisible = visibleProductCount > 0;
    const correlationColSpan = isAnyProductVisible ? 1 : productSecondaryData.length + 1;
    const toggleRowColSpan = visibleProductCount === 0 ? (correlationResult ? productSecondaryData.length + 1 : 1) : (correlationResult ? 1 : 0) + visibleProductCount;

    // Reset product scores to default (pass study title for 1 study, or null for all studies)
    const resetStudyProducts = useCallback(
        (targetStudyTypeMethod = null) => {
            const updatedData = productSecondaryData.map((product) => {
                if (targetStudyTypeMethod && product.studyTypeMethodTitle !== targetStudyTypeMethod) {
                    return product;
                }

                const defaultProduct = defaultProductSecondaryData.find((def) => {
                    const isSameTitle = (def.title || def.product_name)?.trim().toLowerCase() === (product.title || product.product_name)?.trim().toLowerCase();
                    const isSameCoreData = String(def.coreDataId) === String(product.coreDataId);
                    const isSameStudyMethod = def.studyTypeMethodTitle === product.studyTypeMethodTitle;

                    return isSameTitle && isSameCoreData && isSameStudyMethod;
                });

                return defaultProduct ? { ...defaultProduct } : product;
            });

            dispatch(loadProductSecondaryDataState([...updatedData]));
        },
        [productSecondaryData, defaultProductSecondaryData, dispatch]
    );

    const handleApplyCorrelation = ({ correlationData, targetVariable, correlationMethod }) => {
        setCorrelationResult(correlationData);
        setSelectedTargetVariable(targetVariable);
        setSelectedCorrelationMethod(correlationMethod);
        const activeStudies = dataMappingData.map((d) => d.study_type_method_title);
        setActiveCorrelationStudies(activeStudies);
        dispatch(loadCorrelationStudyTypeMethodState(activeStudies));
    };

    const handleResetCorrelation = (studyTypeMethod = null) => {
        if (!studyTypeMethod) {
            setCorrelationResult(null);
            setSelectedTargetVariable(null);
            setSelectedCorrelationMethod(null);
            setActiveCorrelationStudies([]);
            dispatch(loadCorrelationToggleState(false));
            dispatch(loadSelectedCorrelationMethodState(""));
            dispatch(loadSelectedCorrelationTargetVariableState(""));
            dispatch(loadCorrelationStudyTypeMethodState(null));
        } else {
            setActiveCorrelationStudies((prev) => {
                const remaining = prev.filter((title) => title !== studyTypeMethod);
                if (remaining.length === 0) {
                    setCorrelationResult(null);
                    setSelectedTargetVariable(null);
                    setSelectedCorrelationMethod(null);
                    dispatch(loadCorrelationToggleState(false));
                    dispatch(loadSelectedCorrelationMethodState(""));
                    dispatch(loadSelectedCorrelationTargetVariableState(""));
                    dispatch(loadCorrelationStudyTypeMethodState(null));
                } else {
                    dispatch(loadCorrelationStudyTypeMethodState(remaining));
                }
                return remaining;
            });
        }
    };

    // Overall Reset handler (resets correlation, product scores, column visibility & KPIs for all studies)
    const handleOverallReset = () => {
        handleResetCorrelation();
        resetStudyProducts(null);
        dispatch(loadProductsVisibility({}));

        // Reset KPIs & sidebar checkboxes for all studies
        const defaultBackendVariables = dataMappingData.flatMap(({ mapping }) => {
            const defaultMacroCategories = new Set((mapping?.default_mapping?.columns ?? []).map(({ macroCategory }) => macroCategory));

            return (mapping?.all_mapping?.columns ?? []).filter(({ macroCategory }) => !defaultMacroCategories.has(macroCategory)).flatMap(({ displayLabels }) => displayLabels.map(({ backendVariable }) => backendVariable));
        });
        setRemovedKpi(defaultBackendVariables);

        const resetCf = (consumerFilterStr = {}) => {
            const updatedCf = JSON.parse(consumerFilterStr.replaceAll(":true}", ":false}"));
            return updatedCf;
        };

        const resetAllCf = (consumerFilterStr = {}) => {
            const updatedCf = JSON.parse(consumerFilterStr.replaceAll(":true", ":false"));
            return updatedCf;
        };
        let updatedFilters = {};
        Object.keys(consumerFilters).forEach((studyTypeMethod) => {
            updatedFilters[studyTypeMethod] = {
                currentSelectedFilters: resetCf(JSON.stringify(consumerFilters[studyTypeMethod].currentSelectedFilters)),
                currentSelectedAllFilters: resetAllCf(JSON.stringify(consumerFilters[studyTypeMethod].currentSelectedAllFilters)),
                missingFilters: {},
                filterSelectionStatus: false,
                isFilterAcrossProducts: false,
            };
            
        });
        dispatch(loadConsumerFiltersState(updatedFilters));

        // reset performance variables
        // const currentStudyData = dataMappingData.find(({ study_type_method_title }) => study_type_method_title === studyTypeMethod);
        // const defaultCats = new Set((currentStudyData?.mapping?.default_mapping?.columns ?? []).map(({ macroCategory }) => macroCategory));
        // const backendVariables = (currentStudyData?.mapping?.all_mapping?.columns ?? [])
        //     .filter(({ macroCategory }) => !defaultCats.has(macroCategory))
        //     .flatMap(({ displayLabels }) => displayLabels.map(({ backendVariable }) => backendVariable));
        // setSelectedVariables((prev) => ({
        //     ...prev,
        //     [studyTypeMethod]: backendVariables,
        // }));
        const updatedVarObject = Object.fromEntries(dataMappingData.map((datum) => {
            const defaultCats = new Set((datum.mapping?.default_mapping?.columns ?? []).map(({ macroCategory }) => macroCategory));
            const studyRemoved = (datum.mapping?.all_mapping?.columns ?? [])
                .filter(({ macroCategory }) => !defaultCats.has(macroCategory))
                .flatMap(({ displayLabels }) => displayLabels.map(({ backendVariable }) => backendVariable));
            return [datum.study_type_method_title, studyRemoved];
        }));
        setSelectedVariables(updatedVarObject);

        // Reset variable mapping columns for all studies
        const defaultVariableMapping = dataMappingData.map((item) => ({
            study_type_method_title: item.study_type_method_title,
            study_type: item.study_type,
            study_type_method: item.study_type_method,
            study_type_title: item.study_type_title,
            mapping: JSON.parse(JSON.stringify(item.mapping.default_mapping.columns)),
        }));
        setVariableMapping(defaultVariableMapping);

        //Reset correlation state
        dispatch(loadCorrelationToggleState(false));
        dispatch(loadSelectedCorrelationMethodState(""));
        dispatch(loadSelectedCorrelationTargetVariableState(""));
        dispatch(loadCorrelationStudyTypeMethodState(null));

        // Reset interpolation state
        dispatch(loadInterpolationToggleState(false));
        dispatch(loadInterpolationDetailsState({}));

        // Reset weight toggles for all studies
        const resetWeightData = dataMappingData.map((item) => ({
            studyTypeMethodTitle: item.study_type_method_title,
            weightToggle: true,
        }));
        dispatch(loadWeightToggleState(resetWeightData));
    };

    const renderTargetVariableHeaderRow = () => {
        if (!selectedTargetVariable) return null;

        return (
            <tr>
                <TableCellWithInnerSeparator
                    key="target-variable-label"
                    value="Target Variable"
                    cellType="th"
                    testLabel=""
                    mainProps={{ colSpan: 3 }}
                    styles={{
                        main: {
                            minWidth: 500,
                            maxWidth: 500,
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "flex-start",
                            alignItems: "center",
                            color: "#1976d2",
                            fontWeight: 700,
                            fontSize: 13,
                        },
                    }}
                    showSeperator={true}
                />

                <TableCellWithOuterSeparator
                    key="target-variable-value"
                    value={
                        <>
                            <span style={{ color: "#1c1c1c", fontWeight: 500, fontSize: 14 }}>{selectedTargetVariable.label}</span>
                            <span
                                style={{
                                    marginLeft: 6,
                                    fontStyle: "italic",
                                    fontWeight: 600,
                                    color: VariableCategoryColorMap?.[selectedTargetVariable.tag] || "#FA78CF",
                                }}
                            >
                                {selectedTargetVariable.tag}
                            </span>
                        </>
                    }
                    cellType="th"
                    testLabel=""
                    mainProps={{
                        colSpan: toggleRowColSpan,
                    }}
                    styles={{
                        main: {
                            position: "sticky",
                            left: 500,
                            zIndex: 3,
                            fontWeight: "400",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "flex-start",
                            alignItems: "center",
                            paddingLeft: "10px",
                            background: "transparent",
                        },
                    }}
                    showSeperator={true}
                />
            </tr>
        );
    };

    // weight Toggle
    const matchNetWeightToggle = useSelector((state) => state.matchNetStore.weightToggle);
    const parentIdsStoreData = useSelector((state) => state.matchNetStore.parentIds);

    const getDataViewKey = (group) => {
        const studyTypeMethod = group.study_type_method_title;
        const weighted = matchNetWeightToggle.find((item) => item.studyTypeMethodTitle === studyTypeMethod)?.weightToggle;
        const dataViewKey = weighted ? "weight_mean" : "mean";
        return dataViewKey;
    };

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const handleProductClick = (e, info) => {
        setProductDetailsDialogueOpen(true);
        setSelectedProductInfo(info);
    };

    const getActionStandard = useCallback(() => {
        const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False`;
        try {
            api.get(url)
                .then(async (res) => {
                    const response = res.data;
                    setActionStandardData(response);
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [handleError]);

    useEffect(() => {
        getActionStandard();
    }, [getActionStandard]);

    useEffect(() => {
        if (!dataMappingData?.length) return;
        const weightData = dataMappingData.map((item) => ({
            studyTypeMethodTitle: item.study_type_method_title,
            weightToggle: true,
        }));

        dispatch(loadWeightToggleState(weightData));
    }, [dataMappingData]);

    const addBorders = useCallback(() => {
        const table = tableRef.current;
        if (!table) return;

        const BORDER = "2px solid #005eef80";
        table.querySelectorAll("td, th").forEach((cell) => {
            cell.style.borderTop = "none";
            cell.style.borderBottom = "none";
            cell.style.borderLeft = "none";
            cell.style.borderRight = "none";
            cell.style.borderTopLeftRadius = "";
            cell.style.borderTopRightRadius = "";
            cell.style.borderBottomLeftRadius = "";
            cell.style.borderBottomRightRadius = "";
        });

        table.querySelectorAll("tbody.group-body").forEach((tbody) => {
            const rows = Array.from(tbody.querySelectorAll("tr:not(.spacer-row)")).filter((row) => !row.classList.contains("hidden"));

            if (!rows.length) return;

            const lastRowIndex = rows.length - 1;

            let leftRowSpanRemaining = 0;

            rows.forEach((row, rowIndex) => {
                const cells = Array.from(row.children).filter((cell) => cell.tagName === "TD" || cell.tagName === "TH");

                if (!cells.length) return;

                const isFirstRow = rowIndex === 0;
                const isLastRow = rowIndex === lastRowIndex;

                // Top border
                if (isFirstRow) {
                    cells.forEach((cell) => {
                        cell.style.borderTop = BORDER;
                    });
                }

                // Bottom border last row only
                if (isLastRow) {
                    cells.forEach((cell) => {
                        cell.style.borderBottom = BORDER;
                    });
                }

                // Right border
                const visibleCells = cells.filter((cell) => !cell.className.includes("hidden"));
                const lastCell = visibleCells.at(-1) ?? cells.at(-1);
                lastCell.style.borderRight = BORDER;

                if (isFirstRow) lastCell.style.borderTopRightRadius = "8px";
                if (isLastRow) lastCell.style.borderBottomRightRadius = "8px";

                if (leftRowSpanRemaining === 0) {
                    const firstCell = cells[0];

                    firstCell.style.borderLeft = BORDER;
                    if (isFirstRow) firstCell.style.borderTopLeftRadius = "8px";

                    const rowSpan = firstCell.rowSpan || 1;
                    const endsAtRow = rowIndex + rowSpan - 1;

                    if (endsAtRow === lastRowIndex) {
                        firstCell.style.borderBottom = BORDER;
                        firstCell.style.borderBottomLeftRadius = "8px";
                    }

                    leftRowSpanRemaining = rowSpan - 1;
                } else {
                    leftRowSpanRemaining--;
                }
            });
        });
    }, []);

    useEffect(() => {
        let rafId;
        const timer = setTimeout(() => {
            rafId = requestAnimationFrame(() => {
                addBorders();
            });
        }, 50);

        const handleResize = () => {
            addBorders();
        };

        window.addEventListener("resize", handleResize);

        return () => {
            clearTimeout(timer);
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener("resize", handleResize);
        };
    }, [addBorders, dataMappingData, variableMapping, productColumnVisibility, removedKpi, matchNetWeightToggle, correlationResult, productSecondaryData]);

    const ActionStandardsRowData = (product) => {
        const coreDataId = product?.coreDataId;
        const studyTypeTitle = product?.studyTypeTitle || "";
        const studyTypeMethodTitle = product?.studyTypeMethodTitle || "";
        const pro = product?.title || "";

        if (!coreDataId || product?.isCorrelationProduct) {
            return (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={ovalStyleWhite}></div>
                </div>
            );
        }

        // const actionStdCore = actionStandardData.filter((datum) => datum.action_standard_groups.core_data_model_id === coreDataId);
        const actionStdCore = actionStandardData.filter((datum) => datum.action_standard_groups.core_data_model_id === coreDataId && (pro === datum.action_standard_groups.comp_product_code || pro === datum.action_standard_groups.ul_product_code));

        let metData;
        let notMetData;

        if (studyTypeTitle.toLowerCase() === process.env.REACT_APP_FRAGRANCE_TESTING?.toLowerCase()) {
            metData = actionStdCore.filter((datum) => Object.values(JSON.parse(datum?.action_standard_groups?.result) || {})?.[0]?.toLowerCase() === "met") || {};
            notMetData = actionStdCore.filter((datum) => Object.values(JSON.parse(datum?.action_standard_groups?.result) || {})?.[0]?.toLowerCase() !== "met") || {};
        } else {
            metData = actionStdCore.filter((datum) => datum?.action_standard_groups?.result?.toLowerCase() === "met") || {};
            notMetData = actionStdCore.filter((datum) => datum?.action_standard_groups?.result?.toLowerCase() !== "met") || {};
        }

        if (metData.length !== 0 && notMetData.length !== 0) {
            return (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <ActTool
                        data={notMetData}
                        style={ovalStyleRed}
                        studyType={studyTypeTitle}
                        studyTypeMethod={studyTypeMethodTitle}
                    />
                    <ActTool
                        data={metData}
                        style={ovalStyleGreen}
                        studyType={studyTypeTitle}
                        studyTypeMethod={studyTypeMethodTitle}
                    />
                </div>
            );
        } else if (metData.length !== 0) {
            return (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <ActTool
                        data={metData}
                        style={ovalStyleGreen}
                        studyType={studyTypeTitle}
                        studyTypeMethod={studyTypeMethodTitle}
                    />
                </div>
            );
        } else if (notMetData.length !== 0) {
            return (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <ActTool
                        data={notMetData}
                        style={ovalStyleRed}
                        studyType={studyTypeTitle}
                        studyTypeMethod={studyTypeMethodTitle}
                    />
                </div>
            );
        } else {
            return (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div style={ovalStyleWhite}></div>
                </div>
            );
        }
    };

    const returnParentInfoRow = () => {
        const parent = lookupSelectedProducts?.find((item) => parentIdsStoreData?.includes(item?.id)) || lookupSelectedProducts?.[0] || null;
        const parentTitle = parent?.title || "";
        const parentStudyReference = productSecondaryData.find((datum) => datum.title === parentTitle && String(datum.coreDataId) === String(parent?.core_data_model))?.study_reference_title || "";
        const parentLabel = `MatchNet Compatibility Based On - ${parentTitle}${parentStudyReference ? ` / ${parentStudyReference}` : ""}`;
        return (
            <tr>
                <TableCellWithInnerSeparator
                    value={parentLabel}
                    cellType="th"
                    testLabel=""
                    mainProps={{
                        colSpan: 2 + (correlationResult ? 1 : 0),
                        align: "center",
                    }}
                    styles={{
                        main: {
                            minWidth: 200,
                            maxWidth: 200,
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            backgroundColor: "#fff",
                            marginRight: "16px",
                        },
                        wrapper: {},
                        value: {
                            textAlign: "center",
                            justifyContent: "center",
                            alignItems: "center",
                        },
                    }}
                    showSeperator={false}
                />
                {productSecondaryData.map((datum, index) => {
                    let productCodeTitle = datum.title;
                    const productCode = `${datum.title.split(" ").join("_")}__${datum.coreDataId}`;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    const value = lookupSelectedProducts.find((item) => `${item?.title?.split(" ").join("_")}__${item?.core_data_model}` === productCode)?.overall_score;

                    const isParent = parent?.title ? `${parent.title.split(" ").join("_")}__${parent.core_data_model}` === productCode : false;

                    return (
                        <TableCellWithOuterSeparator
                            key={index + 1 + datum.title + "pc"}
                            value={isParent ? "" : `${value}%`}
                            cellType="td"
                            testLabel=""
                            mainProps={{
                                colSpan: 1,
                                className: className,
                            }}
                            styles={{
                                main: {
                                    cursor: "move",
                                    minWidth: 200,
                                    maxWidth: 300,
                                },
                                wrapper: {},
                                value: {
                                    justifyContent: "center",
                                    backgroundColor: isParent ? "#EAEAEA" : "#fff",
                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                    borderRadius: "5px",
                                    padding: "8px",
                                },
                            }}
                        />
                    );
                })}
            </tr>
        );
    };

    const returnTableHeader = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    return i === 0 ? (
                        <TableCellWithInnerSeparator
                            key={col + i}
                            value={col}
                            cellType="th"
                            testLabel=""
                            styles={{
                                main: {
                                    minWidth: 200,
                                    maxWidth: 200,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 2,
                                    backgroundColor: "#fff",
                                },
                                wrapper: {},
                                value: {
                                    alignItems: "center",
                                },
                            }}
                        />
                    ) : (
                        <TableCellWithOuterSeparator
                            key={col + i}
                            value={col}
                            cellType="th"
                            testLabel=""
                            styles={{
                                main: {
                                    minWidth: 300,
                                    maxWidth: 300,
                                    position: "sticky",
                                    left: 200,
                                    zIndex: 2,
                                    backgroundColor: "#fff",
                                },
                                wrapper: {
                                    justifyContent: "space-between",
                                },
                                value: {
                                    alignItems: "center",
                                },
                            }}
                        />
                    );
                })}
                {correlationResult && (
                    <TableCellWithOuterSeparator
                        key="all-products-combined-header"
                        value="All Products Combined"
                        cellType="th"
                        testLabel=""
                        mainProps={{
                            className: "checked--all_products_combined",
                            colSpan: correlationColSpan,
                        }}
                        styles={{
                            main: {
                                position: "sticky",
                                left: 500, // after Attribute (200) + Attribute Area (300)
                                zIndex: 2,
                                fontWeight: 600,
                            },
                            wrapper: { justifyContent: "center" },
                            value: { alignItems: "center", justifyContent: "center" },
                        }}
                    />
                )}
                {productSecondaryData.map((datum, index) => {
                    let productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    let studyReference = datum.study_reference_title;
                    // const showWeightIcon = selectedStudy.length > 1 && selectedStudy?.find((study) => study.id === datum.coreDataId)?.weighted === true;
                    // if (isOralCare) {
                    //     const [title, code] = getOralCareProductCodeTableHeader(datum, productsTestCodeMapData);
                    //     productCodeTitle = title;
                    //     studyReferenceOrTestCode = (
                    //         <>
                    //             {code} / {studyReferenceOrTestCode}
                    //         </>
                    //     );
                    // }

                    return (
                        <TableCellWithOuterSeparator
                            key={index + 1 + datum.title + "pc"}
                            value={
                                <>
                                    {/* {showWeightIcon && (
                                            <img
                                                alt="weightToggle"
                                                style={{
                                                    width: 14,
                                                    marginRight: 10,
                                                }}
                                                src={WeightToggleIcon}
                                            />
                                        )} */}
                                    <button
                                        className="clean-button color-blue"
                                        style={{
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                        }}
                                        onClick={(e) =>
                                            handleProductClick(e, {
                                                coreDataId: datum.coreDataId,
                                                productCode: datum.title,
                                            })
                                        }
                                    >
                                        {productCodeTitle} / {studyReference}
                                    </button>

                                    <HeaderSampleSize productData={datum} />
                                </>
                            }
                            cellType="th"
                            testLabel=""
                            mainProps={{
                                colSpan: 1,
                                align: "center",
                                className: className,
                            }}
                            styles={{
                                main: {
                                    fontWeight: "600",
                                    cursor: "move",
                                    minWidth: 200,
                                    maxWidth: 300,
                                },
                                wrapper: {
                                    justifyContent: "space-between",
                                },
                                value: {
                                    justifyContent: "space-between",
                                    flexDirection: "column",
                                    alignItems: "center",
                                },
                            }}
                        />
                    );
                })}
            </tr>
        );
    };

    const renderTogglesRow = (group) => {
        const studyTypeMethod = group.study_type_method_title;
        const weighted = productSecondaryData.find((item) => item.studyTypeMethodTitle === studyTypeMethod)?.weighted;
        const isVisible = isAnyProductVisible || Boolean(correlationResult);

        return (
            <tr className={`toggle-row ${isVisible ? "" : "hidden"}`}>
                <td
                    colSpan={2}
                    style={{
                        padding: 0,
                        backgroundColor: "#fff",
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        minWidth: 500,
                    }}
                />
                <td
                    colSpan={toggleRowColSpan}
                    style={{ padding: 0, backgroundColor: "#fff" }}
                >
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: "15px", marginRight: "16px" }}>
                        <MatchNetWeightToggle
                            isWeighted={weighted && !(correlationResult && activeCorrelationStudies.includes(studyTypeMethod))}
                            // weightToggle={weightToggle}
                            // setWeightToggle={setWeightToggle}
                            studyTypeMethod={studyTypeMethod}
                        />
                        <FilterAcrossProductsToggleComponent studyTypeMethod={studyTypeMethod} />
                        <div style={{ marginRight: "-1rem" }}>
                            <Reset
                                studyTypeMethod={studyTypeMethod}
                                // setWeightToggle={setWeightToggle}
                                dataMappingData={dataMappingData}
                                variableMapping={variableMapping}
                                setVariableMapping={setVariableMapping}
                                resetStudyProducts={resetStudyProducts}
                                handleResetCorrelation={handleResetCorrelation}
                                setSelectedVariables={setSelectedVariables}
                            />
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    const renderGroupHeaderRow = (group) => {
        const studyTypeMethod = group.study_type_method_title;
        return (
            <tr>
                <TableCellWithOuterSeparator
                    key="jar-overall-penalty-left-header"
                    value={group.study_type_method_title}
                    cellType="td"
                    mainProps={{
                        colSpan: 2,
                    }}
                    styles={{
                        main: { minWidth: 500, maxWidth: 700, fontWeight: "600", position: "sticky", left: 0, zIndex: 2, backgroundColor: "#fff" },
                        wrapper: {},
                        value: { justifyContent: "center", background: "#fff", boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)", fontWeight: "600", borderRadius: "5px", padding: "5px" },
                    }}
                    showSeperator={false}
                />
                {correlationResult && (
                    <td
                        className="checked--all_products_combined"
                        colSpan={correlationColSpan}
                        style={{
                            position: "sticky",
                            left: 500,
                            zIndex: 2,
                            background: "#fff",
                        }}
                    >
                        <p></p>
                    </td>
                )}
                {productSecondaryData.map((datum, index) => {
                    const productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const mappingSTM = group.study_type_method_title;
                    const productSecondarySTM = datum.studyTypeMethodTitle;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    return (
                        <td
                            key={index + 1 + "cf"}
                            colSpan={1}
                            className={className}
                            style={{
                                textAlign: "center",
                                fontWeight: "500",
                                padding: "6px 0px",
                                borderColor: "#fff",
                            }}
                        >
                            {!isConsumerFilterLoadingStoreData ? (
                                <ConsumerFilter
                                    productsSecondaryData={productSecondaryData}
                                    product={datum}
                                    handleFilterSubmit={getCrossStudyComparisonData}
                                    studyTypeMethod={studyTypeMethod}
                                    disableFilter={mappingSTM !== productSecondarySTM}
                                />
                            ) : (
                                <CircularProgress size={20} />
                            )}
                        </td>
                    );
                })}
            </tr>
        );
    };

    const returnAnalysisScoresRow = (group) => {
        const dataViewKey = getDataViewKey(group);
        const isStudyCorrelationActive = Boolean(correlationResult && activeCorrelationStudies.includes(group.study_type_method_title));
        let noOfProducts = productSecondaryData.length;
        const dataMappings = group.mapping;
        const _roww = dataMappings.map((catObj, i) => {
            const kpis = catObj?.displayLabels || [];
            const { rowSpan, productScoreWithNaObj } = getProductScoreWithNaAndRowSpan({ catObj, box: false, key1: null, key2: null, key3: null, isITMDataShown: false, moduleName, productSecondaryData, noOfProducts, removedKpiStoreState: [] });
            // const groductScoreSumObject = getProductScoreSumObject({ catObj, box, key1, key2, key3, isITMDataShown, moduleName, draggableColumns, productColumnVisibility });
            let ifAvailableFirstRow = true;
            const uniqueKpis = new Set();
            const _trs = kpis
                .map((bvObj) => {
                    const rowDisplayLabel = bvObj.displayLabel;
                    const rowBackendVariable = bvObj.backendVariable;
                    let firstColumn = true;
                    if (!uniqueKpis.has(bvObj.displayLabel)) {
                        if (!ifAvailableFirstRow && catObj.displayLabels?.length >= 2) {
                            firstColumn = false;
                        }
                        // const { recallBvObj, itmBvObj } = getRecallAndITM(rowDisplayLabel, rowBackendVariable, moduleName, kpis);

                        const productsScoreWithNA = productScoreWithNaObj[rowDisplayLabel];
                        const rowMacroCategoryLabel = catObj.macroCategory.charAt(0).toUpperCase() + catObj.macroCategory.slice(1);
                        let rowBackendVariableDisplayLabel = bvObj.displayLabel.charAt(0).toUpperCase() + bvObj.displayLabel.slice(1);
                        if (likertScaleData?.[bvObj.backendVariable] || likertScaleData?.[bvObj.backendVariable] == 0) {
                            // const isInterpolatedStudy = interpolationDetailsStoreData?.studyTypeMethod?.title === group.study_type_method_title;
                            const isInterpolatedStudy = interpolationDetailsStoreData?.likertScale !== null;
                            const targetScale = isInterpolatedStudy ? interpolationDetailsStoreData?.likertScale : null;
                            if (targetScale?.length > 0) {
                                rowBackendVariableDisplayLabel += targetScale.toString() !== likertScaleData[bvObj.backendVariable].toString() ? ` (${likertScaleData[bvObj.backendVariable]} → ${targetScale})` : ` (${likertScaleData[bvObj.backendVariable]})`;
                            } else {
                                rowBackendVariableDisplayLabel += ` (${likertScaleData[bvObj.backendVariable]})`;
                            }
                        }
                        // const currentCorrelationRow = correlationLookup?.[rowBackendVariable];
                        // const selectionFilteredData = boxAnalysisSelection[moduleName === "overall" && Object.keys(recallBvObj).length === 0 ? itmBvObj.backendVariable : recallBvObj.backendVariable];
                        const row = (
                            <tr
                                key={i + 1 + "row" + rowBackendVariable}
                                className="score-row"
                            >
                                {firstColumn && (
                                    <TableCellWithInnerSeparator
                                        key={catObj.macroCategory + "first-co" + i}
                                        value={rowMacroCategoryLabel}
                                        cellType="td"
                                        testLabel=""
                                        mainProps={{
                                            rowSpan: rowSpan,
                                        }}
                                        styles={{
                                            main: {
                                                minWidth: 200,
                                                maxWidth: 200,
                                                left: 0,
                                                position: "sticky",
                                                zIndex: 2,
                                                backgroundColor: "#fff",
                                            },
                                            wrapper: {
                                                background: "#fff",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                            value: {
                                                alignItems: "center",
                                            },
                                        }}
                                    />
                                )}
                                <TableCellWithOuterSeparator
                                    value={rowBackendVariableDisplayLabel}
                                    cellType="td"
                                    testLabel=""
                                    styles={{
                                        main: {
                                            left: 200,
                                            position: "sticky",
                                            fontWeight: 500,
                                            zIndex: 2,
                                            backgroundColor: "#fff",
                                        },
                                        wrapper: {},
                                        value: {
                                            background: "#fff",
                                            whiteSpace: "nowrap",
                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                            borderRadius: "5px",
                                            padding: "5px",
                                        },
                                    }}
                                />

                                {/* ── All Products Combined cell ── */}
                                {correlationResult && (
                                    <TableCellWithOuterSeparator
                                        key="all-products-combined-cell"
                                        value={(() => {
                                            if (!isStudyCorrelationActive) return "";
                                            const allProductsCombinedObj = correlationResult?.find((item) => item.title === "All");
                                            const allProductsCombinedRow = allProductsCombinedObj?.score?.find((item) => item.backend_variable === rowBackendVariable);
                                            const hasValue = allProductsCombinedRow?.correlation !== undefined && allProductsCombinedRow?.correlation !== null && allProductsCombinedRow?.correlation !== "NA";
                                            return hasValue ? parseFloat(allProductsCombinedRow.correlation).toFixed(2) : "";
                                        })()}
                                        cellType="td"
                                        mainProps={{
                                            className: "checked--all_products_combined",
                                            colSpan: correlationColSpan,
                                        }}
                                        styles={{
                                            main: {
                                                left: 500,
                                                position: "sticky",
                                                backgroundColor: "#fff",
                                                zIndex: 2,
                                            },
                                            wrapper: {},
                                            value: {
                                                justifyContent: "center",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: (() => {
                                                    if (!isStudyCorrelationActive) return "#fff";
                                                    const allProductsCombinedObj = correlationResult?.find((item) => item.title === "All");
                                                    const allProductsCombinedRow = allProductsCombinedObj?.score?.find((item) => item.backend_variable === rowBackendVariable);
                                                    const hasValue = allProductsCombinedRow?.correlation !== undefined && allProductsCombinedRow?.correlation !== null && allProductsCombinedRow?.correlation !== "NA";
                                                    return hasValue ? getBackgroundColor(allProductsCombinedRow.correlation) : "#EAEAEA";
                                                })(),
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                        }}
                                        showSeperator={true}
                                    />
                                )}

                                {productSecondaryData.map((datum, index) => {
                                    const productCodeTitle = datum.title;
                                    const productCodeCoreDataId = datum.coreDataId;
                                    const mappingSTM = group.study_type_method_title;
                                    const productSecondarySTM = datum.studyTypeMethodTitle;
                                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);

                                    // if (currentCorrelationRow) {
                                    //     const productValueObj = currentCorrelationRow.products?.find((p) => {
                                    //         const apiCode = (p.productCode || "").trim().toLowerCase();
                                    //         const tableCode = (productCodeTitle || "").trim().toLowerCase();
                                    //         return apiCode === tableCode;
                                    //     });
                                    //     const corrValue = productValueObj?.values?.[0];
                                    //     const hasValue = corrValue !== undefined && corrValue !== null && corrValue !== "NA";
                                    //     const displayValue = hasValue ? parseFloat(corrValue).toFixed(2) : "";
                                    //     const bgColor = hasValue ? getBackgroundColor(corrValue) : "#EAEAEA";

                                    //     return (
                                    //         <TableCellWithOuterSeparator
                                    //             key={"corr-" + index}
                                    //             value={displayValue}
                                    //             cellType="td"
                                    //             mainProps={{ className: className }}
                                    //             styles={{
                                    //                 main: { backgroundColor: "#fff" },
                                    //                 wrapper: {},
                                    //                 value: {
                                    //                     justifyContent: "center",
                                    //                     boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                    //                     backgroundColor: bgColor,
                                    //                     borderRadius: "5px",
                                    //                     padding: "5px",
                                    //                 },
                                    //             }}
                                    //             showSeperator={true}
                                    //         />
                                    //     );
                                    // }
                                    if (isStudyCorrelationActive && correlationResult?.length) {
                                        const productObj = correlationResult?.find((item) => item.title === datum.title && item.coreDataId === datum.coreDataId);
                                        const productRow = productObj?.score?.find((item) => item.backend_variable === rowBackendVariable);
                                        const meanScoreObj = getScoreByVariable(datum.scores, rowBackendVariable);
                                        const meanValue = meanScoreObj?.[dataViewKey.split(",")[0]];
                                        const correlationIsNA =productRow?.correlation === undefined ||productRow?.correlation === null ||String(productRow?.correlation).trim().toUpperCase() === "NA";
                                        const meanIsNA =meanValue === undefined ||meanValue === null ||String(meanValue).trim().toUpperCase() === "NA";
                                        const hasValue = productRow?.correlation !== undefined && productRow?.correlation !== null && productRow?.correlation !== "NA";
                                        const displayScore = hasValue ? parseFloat(productRow?.correlation).toFixed(2): correlationIsNA && !meanIsNA? "NA": "";
                                        const bgColor = hasValue ? getBackgroundColor(productRow?.correlation): correlationIsNA && meanIsNA? "#EAEAEA": "#FFFFFF";
                                        return (
                                            <TableCellWithOuterSeparator
                                                key={"corr-missing-" + index}
                                                value={displayScore}
                                                cellType="td"
                                                mainProps={{ className: className }}
                                                styles={{
                                                    main: { backgroundColor: "#fff" },
                                                    wrapper: {},
                                                    value: {
                                                        justifyContent: "center",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        backgroundColor: bgColor,
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                    },
                                                }}
                                                showSeperator={true}
                                            />
                                        );
                                    }

                                    // ── Normal score display ──
                                    let score = null;
                                    const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                                    score = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);

                                    return (
                                        <>
                                            {dataViewKey.split(",").map((ele, k) => {
                                                const key = "score-column-" + index + "-" + k;
                                                const scoreValue = score[dataViewKey.split(",")[k]];
                                                return (
                                                    <TableCellWithOuterSeparator
                                                        key={key}
                                                        value={mappingSTM === productSecondarySTM ? getFormattedScoreOrNA(scoreValue, ele.split(",")[0]) : ""}
                                                        cellType="td"
                                                        mainProps={{
                                                            className: className,
                                                        }}
                                                        styles={{
                                                            main: {
                                                                backgroundColor: "#fff",
                                                            },
                                                            wrapper: {},
                                                            value: {
                                                                justifyContent: "center",
                                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                backgroundColor: mappingSTM === productSecondarySTM ? "#fff" : "#EAEAEA",
                                                                borderRadius: "5px",
                                                                padding: "5px",
                                                            },
                                                        }}
                                                        showSeperator={true}
                                                    />
                                                );
                                            })}
                                        </>
                                    );
                                })}
                            </tr>
                        );
                        uniqueKpis.add(bvObj.displayLabel);
                        if (productsScoreWithNA === noOfProducts) {
                            return null;
                        } else {
                            ifAvailableFirstRow = false;
                            return row;
                        }
                    } else {
                        return;
                    }
                })
                .filter((ele) => ele);
            return _trs;
        });
        return _roww;
    };

    const renderActionStandardsRow = () => (
        <tr>
            <TableCellWithOuterSeparator
                key="jar-overall-penalty-left-header"
                value="Action Standards"
                cellType="td"
                mainProps={{
                    colSpan: 2,
                }}
                styles={{
                    main: { minWidth: 500, maxWidth: 700, fontWeight: "600", position: "sticky", left: 0, zIndex: 2, backgroundColor: "#fff" },
                    wrapper: {},
                    value: { justifyContent: "center", background: "#fff", boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)", fontWeight: "600", borderRadius: "5px", padding: "5px" },
                }}
                showSeperator={false}
            />
            {correlationResult && (
                <TableCellWithOuterSeparator
                    key="action-standards-all-products-combined"
                    value=""
                    cellType="td"
                    mainProps={{
                        className: "checked--all_products_combined",
                        colSpan: correlationColSpan,
                    }}
                    styles={{
                        main: {
                            left: 500,
                            position: "sticky",
                            backgroundColor: "#fff",
                            zIndex: 2,
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            backgroundColor: "#EAEAEA",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={true}
                />
            )}
            {productSecondaryData.map((product, index) => {
                const productCodeTitle = product.title;
                const productCodeCoreDataId = product.coreDataId;
                const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                return (
                    <TableCellWithOuterSeparator
                        key={`as-${product.coreDataId}-${index}`}
                        cellType="td"
                        testLabel=""
                        value={ActionStandardsRowData(product)}
                        mainProps={{
                            className: className,
                        }}
                        styles={{
                            main: { minWidth: 200, maxWidth: 300, backgroundColor: "#fff" },
                            wrapper: {},
                            value: {
                                justifyContent: "center",
                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                borderRadius: "5px",
                                padding: "5px",
                            },
                        }}
                        showSeperator={true}
                    />
                );
            })}
        </tr>
    );

    return (
        <div style={{ position: "relative", padding: "0px 30px 0px 20px" }}>
            <ColumnVisibility
                productsSecondaryData={productSecondaryData}
                pageName="crossStudy"
            />
            <div>
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 8, width: "100%" }}>
                    <Button
                        size="small"
                        variant="contained"
                        className="custom-btn liner-light-blue-background me-4 bold-content"
                        onClick={() => {
                            setIsAddDialogOpen(true);
                        }}
                    >
                        Correlation
                    </Button>
                    <InterpolationDialog variableMapping={variableMapping} />
                    <Button
                        size="small"
                        variant="contained"
                        className="custom-btn liner-light-blue-background me-4 bold-content"
                        onClick={handleOverallReset}
                    >
                        Overall Reset
                    </Button>
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginLeft: 5 }}>
                    <MatchNetSideNavLayout>
                        <SideBar
                            studyMethodsList={dataMappingData}
                            variableMapping={variableMapping}
                            setVariableMapping={setVariableMapping}
                            setRemovedKpi={setRemovedKpi}
                            selectedVariables={selectedVariables}
                            setSelectedVariables={setSelectedVariables}
                        />
                    </MatchNetSideNavLayout>
                    <div
                        className="table-responsive"
                        style={{
                            width: "100%",
                            position: "relative",
                            minHeight: "584px",
                        }}
                    >
                        <table
                            ref={tableRef}
                            className="table text-sm analysis-table"
                            style={{ background: "transparent", borderCollapse: "separate", borderSpacing: 0 }}
                        >
                            <thead>
                                {renderTargetVariableHeaderRow()}

                                {returnParentInfoRow()}
                                {returnTableHeader()}
                            </thead>
                            {variableMapping.map((group) => (
                                <tbody
                                    key={group.study_type_method}
                                    className="group-body"
                                >
                                    {renderTogglesRow(group)}
                                    {renderGroupHeaderRow(group)}
                                    {returnAnalysisScoresRow(group)}
                                    <tr
                                        className="spacer-row"
                                        style={{ height: "8px" }}
                                    >
                                        <td
                                            colSpan={2 + productSecondaryData.length + (correlationResult ? 1 : 0)}
                                            style={{ height: "8px", padding: 0 }}
                                        />
                                    </tr>
                                </tbody>
                            ))}
                            <tbody className="group-body">{renderActionStandardsRow()}</tbody>
                        </table>
                        {/* interpolation tooltip */}
                        <InterpolationDetailsTooltip />
                    </div>
                </div>
                {correlationResult && (
                    <>
                        <ul style={{ display: "flex", justifyContent: "space-around", marginBottom: 0, marginTop: 10, padding: 0 }}>
                            {CORRELATION_COLOR_RANGES.map((range) => (
                                <li
                                    key={range.label}
                                    style={{ listStyle: "none", display: "flex", alignItems: "center" }}
                                >
                                    <span
                                        style={{
                                            height: "15px",
                                            width: "15px",
                                            backgroundColor: range.color,
                                            display: "inline-block",
                                        }}
                                    ></span>
                                    <span style={{ padding: "8px", fontSize: "14px", fontWeight: "400" }}>{range.label}</span>
                                </li>
                            ))}
                        </ul>
                        <p style={{ fontSize: "12px", fontWeight: 600, color: "#495057", marginTop: 4 }}>Note - {selectedCorrelationMethod?.label} is applied</p>
                    </>
                )}
            </div>
            {productDetailsDialogueOpen && (
                <ProductInfo
                    settings={{
                        productDetailsDialogueOpen: productDetailsDialogueOpen,
                        setProductDetailsDialogueOpen: setProductDetailsDialogueOpen,
                    }}
                    info={selectedProductInfo}
                />
            )}
            {isAddDialogOpen && (
                <CorrelationDialogBox
                    isOpen={isAddDialogOpen}
                    setIsAddDialogOpen={setIsAddDialogOpen}
                    productSecondaryData={productSecondaryData}
                    variableMapping={variableMapping}
                    onApplyCorrelation={handleApplyCorrelation}
                />
            )}
        </div>
    );
}

CrossStudyComparisonTable.propTypes = {
    dataMappingData: PropTypes.array,
    productSecondaryData: PropTypes.array,
    variableMapping: PropTypes.array,
    setVariableMapping: PropTypes.func,
    removedKpi: PropTypes.array,
    setRemovedKpi: PropTypes.func,
    getCrossStudyComparisonData: PropTypes.func,
};
export default CrossStudyComparisonTable;
